"use client";

import { formatDateLocal } from "@/shared/lib/date-time";
import { CompactNumber } from "@/shared/ui/CompactNumber";
import { Card, CardContent } from "@/components/ui/card";
import { useMemo } from "react";

export type TopProduct = {
  id: string;
  name: string;
  qtySold: number;
  totalSalesLak: number;
};

export type RecentOrder = {
  id: string;
  orderNo: string;
  branchName: string;
  status: string;
  totalLak: number;
  createdAt: string;
};

export type WalletTransaction = {
  id: string;
  type: string;
  amountLak: number;
  customerName?: string;
  createdAt: string;
};

export type DashboardTablesProps = {
  topProducts?: TopProduct[];
  recentOrders?: RecentOrder[];
  walletTransactions?: WalletTransaction[];
  isLoading?: boolean;
};

export function DashboardTables({
  topProducts,
  recentOrders,
  walletTransactions,
  isLoading,
}: DashboardTablesProps) {
  const displayTopProducts = useMemo(
    () => topProducts?.slice(0, 10) || [],
    [topProducts],
  );

  const displayRecentOrders = useMemo(
    () => recentOrders?.slice(0, 10) || [],
    [recentOrders],
  );

  const displayWalletTransactions = useMemo(
    () => walletTransactions?.slice(0, 10) || [],
    [walletTransactions],
  );

  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="rounded-xl border bg-card">
            <CardContent className="p-6">
              <div className="mb-4 h-6 w-32 animate-pulse rounded bg-muted" />
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((j) => (
                  <div
                    key={j}
                    className="h-12 animate-pulse rounded bg-muted"
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-3">
      {/* Top Products Table */}
      <Card className="rounded-xl border bg-card p-0">
        <CardContent className="p-0">
          <div className="border-b px-4 py-3">
            <h3 className="font-semibold text-lg">ສິນຄ້າຂາຍດີ (Top 10)</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium text-sm">
                    ສິນຄ້າ
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-sm">
                    ຈຳນວນ
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-sm">
                    ຍອດຂາຍ (LAK)
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayTopProducts.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 py-8 text-center text-muted-foreground"
                    >
                      ບໍ່ມີຂໍ້ມູນ
                    </td>
                  </tr>
                ) : (
                  displayTopProducts.map((product, index) => (
                    <tr key={product.id} className="border-b hover:bg-muted/50">
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium">{product.name}</div>
                        <div className="text-muted-foreground text-xs">
                          #{index + 1}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-sm tabular-nums">
                        <CompactNumber
                          value={product.qtySold}
                          formatOptions={{ maximumFractionDigits: 2 }}
                        />
                      </td>
                      <td className="px-4 py-3 text-right text-sm tabular-nums">
                        <CompactNumber value={product.totalSalesLak} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Recent Orders Table */}
      <Card className="rounded-xl border bg-card p-0">
        <CardContent className="p-0">
          <div className="border-b px-4 py-3">
            <h3 className="font-semibold text-lg">ອໍເດີລ່າສຸດ</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium text-sm">
                    ອໍເດີ
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-sm">
                    ສາຂາ
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-sm">
                    ສະຖານະ
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-sm">
                    ຍອດລວມ
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayRecentOrders.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-8 text-center text-muted-foreground"
                    >
                      ບໍ່ມີຂໍ້ມູນ
                    </td>
                  </tr>
                ) : (
                  displayRecentOrders.map((order) => (
                    <tr key={order.id} className="border-b hover:bg-muted/50">
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium font-mono">
                          {order.orderNo}
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {formatDateLocal(order.createdAt)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {order.branchName || "–"}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center rounded-full bg-muted px-2 py-1 text-xs">
                          {order.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-sm tabular-nums">
                        <CompactNumber value={order.totalLak} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Wallet Transactions Table */}
      <Card className="rounded-xl border bg-card p-0">
        <CardContent className="p-0">
          <div className="border-b px-4 py-3">
            <h3 className="font-semibold text-lg">ການເງິນ Wallet ລ່າສຸດ</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium text-sm">
                    ປະເພດ
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-sm">
                    ຈຳນວນ
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-sm">
                    ລູກຄ້າ
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-sm">
                    ວັນທີ
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayWalletTransactions.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-8 text-center text-muted-foreground"
                    >
                      ບໍ່ມີຂໍ້ມູນ
                    </td>
                  </tr>
                ) : (
                  displayWalletTransactions.map((transaction) => (
                    <tr
                      key={transaction.id}
                      className="border-b hover:bg-muted/50"
                    >
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center rounded-full bg-muted px-2 py-1 text-xs">
                          {transaction.type}
                        </span>
                      </td>
                      <td
                        className={`px-4 py-3 text-right text-sm tabular-nums ${
                          transaction.amountLak >= 0
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        <CompactNumber
                          value={transaction.amountLak}
                          prefix={transaction.amountLak >= 0 ? "+" : ""}
                          suffix=" LAK"
                          className={
                            transaction.amountLak >= 0
                              ? "text-green-600"
                              : "text-red-600"
                          }
                        />
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {transaction.customerName || "–"}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {formatDateLocal(transaction.createdAt)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
